import {
	Environment,
	RecordSource,
	RelayFeatureFlags,
	type EnvironmentConfig,
	type IEnvironment,
} from 'relay-runtime';
// @ts-ignore
import LiveResolverStore from 'relay-runtime/lib/store/live-resolvers/LiveResolverStore';
import { defaultDataIdFromObject } from 'apollo-cache-inmemory';

import RelayMissingFieldHandlers from '@atlassian/relay-missing-field-handlers';
import RelayScheduler from '@atlassian/relay-scheduler';

import { network, noopNetwork } from '@confluence/relay-network/entry-points/network.ts';

// Enable Relay Resolvers globally
RelayFeatureFlags.ENABLE_RELAY_RESOLVERS = true;

export type RelayEnvironmentOptions = {
	disableNetwork?: boolean;
};

const defaultRelayEnvironmentOptions: RelayEnvironmentOptions = {
	disableNetwork: false,
};

const getConfluenceDataID = (fieldValue: { [key: string]: unknown }, typeName: string) => {
	if (typeName === 'WebItem') {
		return `${fieldValue.completeKey || fieldValue.id}; ${fieldValue.url}`;
	} else if (typeName === 'OnboardingState') {
		return `${fieldValue.key}`;
	} else if (typeName === 'WebSection') {
		return `${fieldValue.cacheKey}` || `${String(fieldValue.id)}`;
	} else if (typeName === 'DetailsSummaryLines') {
		return `${fieldValue.macroId}_${fieldValue.currentPage}`;
	} else if (typeName === 'DetailLine') {
		return `${fieldValue.id}_${fieldValue.rowId}_${fieldValue.macroId}`;
	} else if (typeName === 'ConfluenceSystemTemplate') {
		return `${fieldValue.moduleCompleteKey}`;
	}

	// Apollo-style global fallback: key records by `${__typename}:${id}` so that
	// Relay's store identity matches Apollo's defaultDataIdFromObject convention.
	//
	// Why we cannot use `defaultDataIdFromObject(fieldValue)` directly: the Relay
	// normalizer is invoked on the raw server response, which (unlike Apollo) does
	// NOT carry `__typename` on every selection — Relay omits `__typename` from
	// outgoing operations whenever the compiler can derive the type statically.
	// As a result `fieldValue.__typename` is undefined for most records and
	// `defaultDataIdFromObject` returns null, causing Relay to fall back to the
	// synthetic storage-path DataID (e.g. `client:root:...:nodes:0`). That makes
	// downstream `readFragment` / `writeFragment` callers that use the Apollo
	// convention (`${typeName}:${id}`) silently miss the record.
	//
	// `typeName` is supplied separately by Relay from the compiled schema type,
	// so we use it instead and preserve Apollo-compatible identity.
	if (fieldValue.id != null) {
		return `${typeName}:${fieldValue.id}`;
	}
	return defaultDataIdFromObject(fieldValue);
};

/**
 * Provides the relay environment config for Confluence.
 * This inlines the logic previously provided by the deprecated getDefaultRelayEnvironmentConfig
 * from @atlassian/relay-environment-config, with Confluence-specific overrides applied.
 *
 * @returns The relay environment config for Confluence.
 */
export function getConfluenceRelayEnvironmentConfig(
	relayEnvironmentOptions: RelayEnvironmentOptions = defaultRelayEnvironmentOptions,
): EnvironmentConfig {
	const recordSource = new RecordSource(window.__RELAY_STORE__);
	return {
		configName: 'Atlassian Graphql Gateway - Confluence',
		network: relayEnvironmentOptions.disableNetwork ? noopNetwork : network,
		store: new LiveResolverStore(recordSource, {
			getDataID: getConfluenceDataID,
		}),
		getDataID: getConfluenceDataID,
		scheduler: RelayScheduler,
		missingFieldHandlers: RelayMissingFieldHandlers,
	};
}

/**
 * Gets the relay environment for Confluence. This function is exported to allow the relay environment to be used in tests.
 *
 * @returns The relay environment for Confluence.
 */

let relayEnvironment: IEnvironment | null = null;
export function getRelayEnvironment(
	relayEnvironmentOptions: RelayEnvironmentOptions = defaultRelayEnvironmentOptions,
): IEnvironment {
	if (window['GLOBAL_RELAY_ENV']) {
		return window['GLOBAL_RELAY_ENV'];
	}
	if (!relayEnvironment) {
		relayEnvironment = new Environment(
			getConfluenceRelayEnvironmentConfig(relayEnvironmentOptions),
		);
	}
	window['GLOBAL_RELAY_ENV'] = relayEnvironment;
	return relayEnvironment;
}

export function resetRelayEnvironment(): void {
	relayEnvironment = null;
	window['GLOBAL_RELAY_ENV'] = undefined;
}
